// src/components/PrivacyPolicy.jsx
// Full-screen Privacy Policy page. Same structure as TermsOfService.jsx.
const BG2   = "#111111";
const BDR2  = "#242424";
const TEXT  = "#f0f0f0";
const MUTED = "#888888";
const FAINT = "#555555";
const MONO  = "var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)";
const DISP  = "var(--font-display, 'Playfair Display', Georgia, serif)";
const GREEN = "var(--accent, #3ddc84)";

function Section({ number, title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, color: GREEN, letterSpacing: ".15em", flexShrink: 0 }}>
          {String(number).padStart(2, "0")}
        </span>
        <h2 style={{ fontFamily: DISP, fontSize: 16, letterSpacing: ".08em", color: TEXT, margin: 0 }}>
          {title.toUpperCase()}
        </h2>
      </div>
      <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, lineHeight: 1.9, paddingLeft: 36 }}>
        {children}
      </div>
    </div>
  );
}

export default function PrivacyPolicy({ onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg, #0a0a0a)", fontFamily: MONO, color: TEXT }}>

      {/* Sticky top bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: "rgba(10,10,10,0.96)", backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${BDR2}`,
        height: 52,
        display: "flex", alignItems: "center", padding: "0 32px", gap: 12,
      }}>
        <span style={{ fontFamily: DISP, fontSize: 15, letterSpacing: ".1em", color: TEXT }}>
          NUVPORT<span style={{ color: GREEN }}> OS</span>
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onBack}
          style={{
            background: "none", border: `1px solid ${BDR2}`,
            color: MUTED, fontFamily: MONO, fontSize: 10,
            letterSpacing: ".08em", padding: "6px 14px", cursor: "pointer",
            transition: "color .15s, border-color .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = TEXT; e.currentTarget.style.borderColor = MUTED; }}
          onMouseLeave={e => { e.currentTarget.style.color = MUTED; e.currentTarget.style.borderColor = BDR2; }}
        >
          ← BACK
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "60px 32px 100px" }}>

        {/* Header */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 9, letterSpacing: ".2em", color: GREEN, marginBottom: 12 }}>
            // LEGAL
          </div>
          <h1 style={{ fontFamily: DISP, fontSize: "clamp(36px,5vw,60px)", letterSpacing: ".04em", color: TEXT, margin: "0 0 16px" }}>
            PRIVACY POLICY
          </h1>
          <div style={{ fontSize: 10, color: FAINT, letterSpacing: ".08em" }}>
            Effective date: March 2026 · nuvport.com
          </div>
        </div>

        <Section number={1} title="Information We Collect">
          <p>We collect the following information when you use Nuvport OS:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
            <li><strong style={{ color: TEXT }}>Account information:</strong> Email address and hashed password (or Google account identifier for OAuth users)</li>
            <li><strong style={{ color: TEXT }}>Portfolio data:</strong> Ticker symbols, portfolio weights, and any custom holdings you add to the Service</li>
            <li><strong style={{ color: TEXT }}>Usage data:</strong> Which features you access and general interaction patterns, used to improve the Service</li>
            <li><strong style={{ color: TEXT }}>Legal acceptance:</strong> Timestamp of Terms of Service and Privacy Policy acceptance</li>
            <li><strong style={{ color: TEXT }}>Subscription data:</strong> Billing plan and Stripe customer ID (if you subscribe to a paid plan)</li>
          </ul>
          <p style={{ marginTop: 12 }}>We do not collect financial account credentials, brokerage login information, or payment card details (handled entirely by Stripe).</p>
        </Section>

        <Section number={2} title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
            <li>Provide, maintain, and improve the Service</li>
            <li>Authenticate your account and restore your portfolio data across sessions and devices</li>
            <li>Process subscription payments and manage billing</li>
            <li>Send transactional emails (email confirmation, password reset)</li>
            <li>Respond to support requests</li>
            <li>Comply with legal obligations and maintain audit trails (including TOS acceptance timestamps)</li>
          </ul>
          <p style={{ marginTop: 12 }}>We do not sell your personal information to third parties. We do not use your portfolio holdings for any purpose other than rendering the Service to you.</p>
        </Section>

        <Section number={3} title="Data Storage">
          <p>Your account and portfolio data is stored in Supabase, a managed cloud database service. Supabase stores data on infrastructure hosted in the EU or US depending on your project region.</p>
          <p style={{ marginTop: 12 }}>Data is encrypted at rest and in transit. Supabase implements Row Level Security (RLS) so that each user can only access their own data.</p>
          <p style={{ marginTop: 12 }}>For more information on Supabase's data handling, see: <span style={{ color: GREEN }}>supabase.com/privacy</span></p>
        </Section>

        <Section number={4} title="Third-Party Services">
          <p>Nuvport OS integrates with the following third-party services to provide functionality:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
            <li><strong style={{ color: TEXT }}>Yahoo Finance:</strong> Live market quotes, historical prices, and company data. Requests are proxied through our servers; your data is not shared with Yahoo Finance.</li>
            <li><strong style={{ color: TEXT }}>Alpha Vantage:</strong> Supplementary financial data. Same proxy arrangement applies.</li>
            <li><strong style={{ color: TEXT }}>Groq (AI analysis):</strong> Portfolio and news analysis features send anonymised financial data (ticker symbols, metrics) to Groq's API. No personally identifiable information is included.</li>
            <li><strong style={{ color: TEXT }}>Stripe:</strong> Payment processing for Pro and Institutional subscriptions. Stripe stores your payment card details and billing information. We do not have access to your raw card data.</li>
            <li><strong style={{ color: TEXT }}>Google OAuth:</strong> If you sign in with Google, we receive your Google account email and a unique identifier. We do not receive access to your Google data beyond authentication.</li>
          </ul>
        </Section>

        <Section number={5} title="Cookies & Local Storage">
          <p>Nuvport OS uses browser <strong style={{ color: TEXT }}>localStorage</strong> (not cookies) to store your portfolio state, user preferences, and onboarding completion status. This data is stored locally on your device and is not transmitted to our servers except as part of the normal Supabase sync.</p>
          <p style={{ marginTop: 12 }}>We use Supabase's authentication cookies for session management. These are strictly necessary for the Service to function.</p>
          <p style={{ marginTop: 12 }}>We do not use advertising cookies, analytics cookies, or any tracking cookies.</p>
        </Section>

        <Section number={6} title="Your Rights">
          <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
            <li><strong style={{ color: TEXT }}>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong style={{ color: TEXT }}>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong style={{ color: TEXT }}>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong style={{ color: TEXT }}>Portability:</strong> Request your portfolio data in a machine-readable format (CSV export)</li>
            <li><strong style={{ color: TEXT }}>Objection:</strong> Object to certain processing of your data</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise any of these rights, contact us at <span style={{ color: GREEN }}>nuvportos@gmail.com</span>. We will respond within 30 days.</p>
        </Section>

        <Section number={7} title="Data Retention">
          <p>We retain your account and portfolio data for as long as your account is active. If you delete your account, we will delete or anonymise your personal data within 30 days, except where we are required to retain it for legal or compliance purposes (such as billing records).</p>
          <p style={{ marginTop: 12 }}>TOS acceptance timestamps are retained for the lifetime of the account as part of our legal compliance audit trail.</p>
        </Section>

        <Section number={8} title="Children's Privacy">
          <p>Nuvport OS is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us at <span style={{ color: GREEN }}>nuvportos@gmail.com</span> and we will delete that information.</p>
        </Section>

        <Section number={9} title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the effective date at the top of this page. Your continued use of the Service after any changes constitutes your acceptance of the updated policy.</p>
        </Section>

        <Section number={10} title="Contact">
          <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
          <p style={{ marginTop: 12, color: GREEN }}>nuvportos@gmail.com</p>
          <p style={{ marginTop: 8 }}>nuvport.com · Nuvport OS</p>
        </Section>

      </div>
    </div>
  );
}
